import {
  ALL_STATUSES,
  CodingSummaryCard,
  DataStatusNote,
  formatAcademicYear,
  formatClassSection,
  formatDate,
  formatDuration,
  formatPercent,
  formatScore,
  galleryItemsToMedia,
  GalleryMedia,
  GIL,
  GIL_CELL,
  GilColumn,
  GilGallery,
  GilReportTable,
  initialsOf,
  LearningOutcomesCard,
  matchesReportFilters,
  NoContentCard,
  OverviewSection,
  performanceLabel,
  projectLabel,
  ReportFilters,
  ReportLayout,
  ReportTabs,
  SectionCard,
  StatTile,
  StatusChip,
  TimeSpentCard,
  toPercent,
  useCommonServiceClientContext,
  useGilReport,
} from '@geneo2-web/shared-ui';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CodeIcon from '@mui/icons-material/Code';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Box, Typography } from '@mui/material';
import {
  GilProjectStatusEnum,
  GilStudentProjectRow,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GIL_Reports_Student_Project } from '../../../routeHandling/RoutesNomenclature';

type JourneyTab = 'projects' | 'evidence';

const TABS = [
  { key: 'projects', label: 'Projects', icon: <FolderOutlinedIcon /> },
  { key: 'evidence', label: 'Evidence', icon: <ImageOutlinedIcon /> },
];

/**
 * The work credited to the student on a project: their group's shared lab
 * device on a group project, their own sittings otherwise.
 */
const creditedTime = (row: GilStudentProjectRow) =>
  row.groupWork?.timeSpent ?? row.timeSpent;

/** Dashboard 2 - one student's journey across every GIL project assigned to them. */
export const GilStudentJourneyReport = () => {
  const navigate = useNavigate();
  const { student_id } = useParams();
  const [searchParams] = useSearchParams();
  //~ Optional; the server defaults to the year of the student's current class.
  const academicYearId =
    Number(searchParams.get('academicYearId')) || undefined;
  const { LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [tab, setTab] = useState<JourneyTab>('projects');

  const { state, reload } = useGilReport(
    () =>
      student_id && /^\d+$/.test(student_id)
        ? LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getGilStudentJourneyReport(
            { studentId: BigInt(student_id), academicYearId }
          ).then((response) => response.data)
        : undefined,
    [student_id, academicYearId]
  );

  const report = state.status === 'success' ? state.data : undefined;
  const summary = report?.summary;
  //~ Credited: includes the group's shared-device time on group projects.
  const totalTime = summary?.creditedTimeSpent ?? summary?.totalTimeSpent;
  const projects = useMemo(() => report?.projects ?? [], [report?.projects]);
  const assignedCount = summary?.projectsAssigned ?? 0;
  const completedCount = summary?.projectsCompleted ?? 0;
  const totalSec = totalTime?.totalTimeInSec ?? 0;
  const codingSec = totalTime?.codingTimeInSec ?? 0;
  const studentName = report?.studentName || 'The student';
  //~ Each project's evidence sits on its own row, not in one pooled gallery.
  const evidenceByProject = useMemo(() => {
    const byProject = new Map<bigint, GalleryMedia[]>();
    (report?.gallery ?? []).forEach((item) => {
      byProject.set(item.assessmentId, [
        ...(byProject.get(item.assessmentId) ?? []),
        ...galleryItemsToMedia([item], false),
      ]);
    });
    return byProject;
  }, [report?.gallery]);

  const visibleProjects = useMemo(
    () =>
      projects.filter((row) =>
        matchesReportFilters(
          projectLabel(row.project),
          row.status,
          search,
          statusFilter
        )
      ),
    [projects, search, statusFilter]
  );

  const openProject = (row: GilStudentProjectRow) => {
    if (student_id && row.project?.assessmentId) {
      navigate(
        `${GIL_Reports_Student_Project}/${student_id}/${row.project.assessmentId}`
      );
    }
  };

  const notStarted = (row: GilStudentProjectRow) =>
    row.status === GilProjectStatusEnum.GIL_PROJECT_STATUS_NOT_STARTED;

  const columns: GilColumn<GilStudentProjectRow>[] = [
    {
      key: 'index',
      header: '#',
      render: (_row, index) => (
        <Typography sx={GIL_CELL.index}>{index + 1}</Typography>
      ),
    },
    {
      key: 'project',
      header: 'Project',
      minWidth: 240,
      //~ The underlined name is the drill-down into the student's project report.
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography
            role="link"
            tabIndex={0}
            onClick={() => openProject(row)}
            onKeyDown={(event) => event.key === 'Enter' && openProject(row)}
            sx={GIL_CELL.link}
          >
            {projectLabel(row.project)}
          </Typography>
          <Typography sx={GIL_CELL.sub}>
            Assigned {formatDate(row.project?.assignedDate)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'group',
      header: 'Group Name',
      render: (row) => (
        <Typography noWrap sx={GIL_CELL.strong}>
          {row.groupName || '—'}
        </Typography>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusChip status={row.status} />,
    },
    {
      key: 'total',
      header: 'Total Time',
      render: (row) =>
        notStarted(row) ? (
          <Typography noWrap sx={GIL_CELL.sub}>
            Not Started
          </Typography>
        ) : (
          formatDuration(creditedTime(row)?.totalTimeInSec)
        ),
    },
    {
      key: 'video',
      header: 'Resources',
      render: (row) =>
        notStarted(row)
          ? '—'
          : formatDuration(creditedTime(row)?.videoActivityBookTimeInSec),
    },
    {
      key: 'coding',
      header: 'Coding',
      render: (row) =>
        notStarted(row)
          ? '—'
          : formatDuration(creditedTime(row)?.codingTimeInSec),
    },
    {
      key: 'score',
      header: 'Project Score',
      render: (row) => formatScore(row.projectScore),
    },
  ];

  return (
    <ReportLayout
      title={report ? `${report.studentName}'s GIL Journey` : 'GIL Journey'}
      avatarText={initialsOf(report?.studentName) || undefined}
      avatarVariant="circle"
      meta={
        report && [
          {
            value: 'Student',
            iconName: 'user',
            color: '#EEF0FF',
            textColor: '#2E3192',
            bold: true,
          },
          {
            label: 'Class',
            value: formatClassSection(report.classSection),
            color: '#FFF8E6',
            textColor: GIL.text,
            bold: true,
          },
          {
            label: 'Academic Year',
            value: formatAcademicYear(report.classSection?.academicYear),
          },
        ]
      }
      state={state}
      onRetry={reload}
    >
      <DataStatusNote status={report?.dataStatus} />

      <OverviewSection subtitle="Quick summary of the student's activity and performance">
        <StatTile
          tone="green"
          icon={<CheckCircleOutlineIcon />}
          value={`${completedCount}/${assignedCount}`}
          label="Projects Completed"
          caption={
            assignedCount
              ? `${Math.round(
                  toPercent(completedCount, assignedCount)
                )}% completion`
              : 'No projects yet'
          }
        />
        <StatTile
          tone="blue"
          icon={<AccessTimeIcon />}
          value={formatDuration(totalSec)}
          label="Total Time Spent"
          caption="Across all activities"
        />
        <StatTile
          tone="purple"
          icon={<CodeIcon />}
          value={formatDuration(codingSec)}
          label="Coding Time"
          caption={
            totalSec
              ? `${Math.round(toPercent(codingSec, totalSec))}% of total time`
              : 'No coding yet'
          }
        />
        <StatTile
          tone="red"
          icon="gil-star-red"
          value={formatPercent(summary?.avgProjectScorePercent)}
          label="Avg. Project Score"
          caption={performanceLabel(summary?.avgProjectScorePercent)}
        />
      </OverviewSection>

      <TimeSpentCard
        videoSec={totalTime?.videoActivityBookTimeInSec}
        codingSec={codingSec}
        subtitle="Group projects count the group's shared-device time"
        effortText={`${studentName} is putting consistent effort into their learning journey.`}
      />

      <ReportTabs
        tabs={TABS}
        value={tab}
        onChange={(key) => setTab(key as JourneyTab)}
      />

      {tab === 'projects' && (
        <>
          <SectionCard
            plain
            title="Projects"
            subtitle="Progress and performance across all assigned projects"
            action={
              projects.length > 0 && (
                <ReportFilters
                  search={search}
                  onSearchChange={setSearch}
                  status={statusFilter}
                  onStatusChange={setStatusFilter}
                />
              )
            }
          >
            {projects.length === 0 ? (
              <NoContentCard
                variant="info"
                icon="laboratory"
                text="No GIL projects have been assigned to this student yet"
              />
            ) : (
              <GilReportTable
                columns={columns}
                rows={visibleProjects}
                rowKey={(row) => String(row.project?.assessmentId ?? 0)}
                emptyText="No projects match your search"
              />
            )}
          </SectionCard>
          <LearningOutcomesCard
            outcomes={report?.learningOutcomes}
            showProjects
            subtitle="How the student is doing on each learning outcome across projects"
          />
          {/* Credited: one entry per project coded in (with the group on group
              projects), so the averages read as per-project means. */}
          <CodingSummaryCard
            summary={summary?.creditedCodingSummary ?? summary?.codingSummary}
            title="Coding Journey"
            subtitle="Across every project the student has coded in"
          />
        </>
      )}

      {tab === 'evidence' &&
        (projects.length === 0 ? (
          <SectionCard plain>
            <NoContentCard
              variant="info"
              icon="laboratory"
              text="No GIL projects have been assigned to this student yet"
            />
          </SectionCard>
        ) : (
          //~ One gallery per project, so each upload is seen against its project.
          projects.map((row) => (
            <SectionCard
              key={String(row.project?.assessmentId ?? 0)}
              plain
              title={projectLabel(row.project)}
              subtitle={[
                row.groupName,
                `Assigned ${formatDate(row.project?.assignedDate)}`,
              ]
                .filter(Boolean)
                .join(' | ')}
              action={<StatusChip status={row.status} />}
            >
              <GilGallery
                items={
                  evidenceByProject.get(
                    row.project?.assessmentId ?? BigInt(0)
                  ) ?? []
                }
                title={projectLabel(row.project)}
              />
            </SectionCard>
          ))
        ))}
    </ReportLayout>
  );
};

export default GilStudentJourneyReport;
